import type { StatutCommande } from './types'

/**
 * Choix du livreur à assigner : distance au marchand, tri, éligibilité.
 *
 * La règle d'affectation elle-même reste au serveur (`set-order-status` : claim
 * atomique, livreur `actif` exigé depuis la 0045). Ici on ne fait que présenter
 * les candidats dans un ordre utile.
 */

export interface Point {
  lat: number
  lng: number
}

/** (0, 0) est la « position » écrite par défaut quand rien n'a été relevé : elle ne compte pas. */
export function positionConnue(lat: number | null | undefined, lng: number | null | undefined): boolean {
  return (
    typeof lat === 'number' && typeof lng === 'number' &&
    Number.isFinite(lat) && Number.isFinite(lng) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180 &&
    !(lat === 0 && lng === 0)
  )
}

const R_TERRE_KM = 6371

/** Distance à vol d'oiseau (haversine), en km. Ce n'est pas le trajet routier. */
export function distanceKm(a: Point, b: Point): number {
  const rad = (d: number) => (d * Math.PI) / 180
  const dLat = rad(b.lat - a.lat)
  const dLng = rad(b.lng - a.lng)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R_TERRE_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

export interface CandidatLivreur {
  id: string
  nom: string
  lat: number | null
  lng: number | null
}

/** Le candidat, avec sa distance au point donné (`null` si l'un des deux est inconnu). */
export type CandidatAvecDistance<T extends CandidatLivreur> = T & { distanceKm: number | null }

/**
 * Du plus proche au plus loin ; les positions inconnues à la fin (par nom, pour un
 * ordre stable). Sans origine connue (marchand sans position), tous sont « inconnus ».
 */
export function trierParDistance<T extends CandidatLivreur>(
  liste: readonly T[],
  origine: Point | null,
): Array<CandidatAvecDistance<T>> {
  const avec = liste.map((l) => ({
    ...l,
    distanceKm:
      origine && positionConnue(l.lat, l.lng)
        ? distanceKm(origine, { lat: l.lat as number, lng: l.lng as number })
        : null,
  }))
  return avec.sort((a, b) => {
    if (a.distanceKm !== null && b.distanceKm !== null) return a.distanceKm - b.distanceKm || a.nom.localeCompare(b.nom, 'fr')
    if (a.distanceKm !== null) return -1
    if (b.distanceKm !== null) return 1
    return a.nom.localeCompare(b.nom, 'fr')
  })
}

/** « 850 m », « 1,4 km », « 12 km » ; « position inconnue » sinon. */
export function libelleDistance(km: number | null): string {
  if (km === null) return 'position inconnue'
  if (km < 1) return `${Math.max(10, Math.round((km * 1000) / 10) * 10)} m`
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`
  return `${Math.round(km)} km`
}

/** Âge du dernier relevé en minutes, `null` si inconnu. */
export function ageMinutes(releveLe: string | null, maintenant: number = Date.now()): number | null {
  if (!releveLe) return null
  const t = new Date(releveLe).getTime()
  if (!Number.isFinite(t)) return null
  return Math.max(0, Math.round((maintenant - t) / 60000))
}

/** « il y a 12 min », « il y a 3 h » — seulement quand le relevé n'est plus frais (> 15 min). */
export function mentionAnciennete(minutes: number | null): string {
  if (minutes === null || minutes <= 15) return ''
  if (minutes < 120) return `relevée il y a ${minutes} min`
  if (minutes < 60 * 48) return `relevée il y a ${Math.round(minutes / 60)} h`
  return `relevée il y a ${Math.round(minutes / 1440)} j`
}

/**
 * Peut-on proposer « Assigner » ? Seulement à partir de l'acceptation par le
 * marchand (accepted, preparing, ready) et tant qu'aucun livreur n'est posé :
 * c'est ce que permet `set-order-status` (rider_assigned).
 */
export function peutAssigner(statut: StatutCommande, livreurId: string | null): boolean {
  return !livreurId && (statut === 'accepted' || statut === 'preparing' || statut === 'ready')
}

/**
 * Retire de la liste le CLIENT de la commande (migration 0053 : un livreur peut
 * commander avec son compte, mais ne livre jamais sa propre commande). Le
 * serveur le refuse de toute façon (`livreur_client_de_la_commande`) ; le
 * proposer ferait échouer « Premier disponible » pour rien.
 */
export function sansLeClient<T extends { id: string }>(liste: readonly T[], clientId: string | null | undefined): T[] {
  return clientId ? liste.filter((l) => l.id !== clientId) : [...liste]
}
